import type { ILine, IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import type { BaseTool } from '@/plugins/cad/module/core/tool/BaseTool'

/** 捕捉类型 */
export enum CaptureType {
  /** 不捕捉 */
  None = 'none',
  /** 端点 */
  Endpoint = 'endpoint',
  /** 中点 */
  Midpoint = 'midpoint',
  /** 节点 */
  Node = 'node',
  /** 圆心 */
  Center = 'center',
  /** 最近点 */
  Nearest = 'nearest',
}

/** 捕捉结果 */
export interface ICaptureResult<T = any> {
  /** 捕捉到的点 */
  point: IPoint
  /** 捕捉类型 */
  type: CaptureType
  /** 关联的实体 */
  entity?: T | undefined
  /** 距离鼠标的距离 */
  distance: number
}

/** 捕捉配置 */
export interface ICaptureConfig {
  /** 是否启用捕捉 */
  enabled: boolean
  /** 捕捉半径（屏幕像素） */
  captureRadius: number
  /** 启用的捕捉类型 */
  enabledTypes: Set<CaptureType>
  /** 是否显示捕捉标记 */
  showMarker: boolean
}

/** 捕捉数据（包含点和线） */
export interface ICaptureSourceData {
  /** 可被捕捉的离散点（端点、中点、圆心等） */
  points: IPoint[]
  /** 可被捕捉的线段（用于最近点捕捉） */
  lines: ILine[]
}

/**
 * 捕捉器抽象基类，约束各种几何捕捉策略的通用接口与配置。
 *
 * @typeParam T - 参与捕捉的实体类型
 */
export abstract class Capturer<T> {
  /** 默认配置 */
  protected defaultConfig: ICaptureConfig = {
    enabled: false,
    captureRadius: 10, // 10像素
    enabledTypes: new Set([
      CaptureType.Endpoint,
      CaptureType.Midpoint,
      CaptureType.Node,
      CaptureType.Center,
      CaptureType.Nearest,
    ]),
    showMarker: true,
  }

  /** 当前配置 */
  protected config: ICaptureConfig = { ...this.defaultConfig }

  /** 最后一次捕捉的结果 */
  protected lastCaptureResult: ICaptureResult<T> | null = null

  /** 当前宿主工具实例，用于与工具上下文交互 */
  tool: BaseTool | undefined

  /**
   * 设置捕捉配置。
   *
   * @param config - 需要合并到当前配置的字段
   */
  setConfig(config: Partial<ICaptureConfig>): void {
    this.config = { ...this.config, ...config }
    if (config.enabledTypes) {
      this.config.enabledTypes = new Set(config.enabledTypes)
    }
  }

  /**
   * 获取当前配置。
   *
   * @returns 当前捕捉配置的浅拷贝
   */
  getConfig(): ICaptureConfig {
    return { ...this.config }
  }

  /**
   * 启用/禁用捕捉。
   *
   * @param enabled - true 表示启用，false 表示禁用
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled
  }

  /**
   * 是否启用了捕捉。
   *
   * @returns 启用返回 true，否则返回 false
   */
  isEnabled(): boolean {
    return this.config.enabled
  }

  /**
   * 设置捕捉半径。
   *
   * @param radius - 捕捉半径，单位屏幕像素，最小为 1
   */
  setCaptureRadius(radius: number): void {
    this.config.captureRadius = Math.max(1, radius)
  }

  /**
   * 启用/禁用特定捕捉类型。
   *
   * @param type - 捕捉类型
   * @param enabled - true 表示启用该类型，false 表示禁用
   */
  setCaptureType(type: CaptureType, enabled: boolean): void {
    if (enabled) {
      this.config.enabledTypes.add(type)
    } else {
      this.config.enabledTypes.delete(type)
    }
  }

  /**
   * 检查是否启用了指定的捕捉类型。
   *
   * @param type - 待检查的捕捉类型
   * @returns 启用返回 true，否则返回 false
   */
  isCaptureTypeEnabled(type: CaptureType): boolean {
    return this.config.enabledTypes.has(type)
  }

  /**
   * 获取最后一次捕捉结果。
   *
   * @returns 最近一次捕捉结果；尚未捕捉或已清除时返回 null
   */
  getLastCaptureResult(): ICaptureResult<T> | null {
    return this.lastCaptureResult
  }

  /**
   * 清除捕捉结果
   */
  clearCapture(): void {
    this.lastCaptureResult = null
  }

  /**
   * 捕捉点
   * @param mousePoint 鼠标位置（屏幕坐标）
   * @param worldPoint 鼠标对应的世界坐标
   * @param entities 要检测捕捉的实体列表
   * @returns 捕捉结果，如果没有捕捉到则返回null
   */
  abstract capture(mousePoint: IPoint, worldPoint: IPoint, entities?: T[]): ICaptureResult<T> | null

  /**
   * 计算点到线段的最近点
   * @param point 目标点
   * @param line 线段
   * @returns 线段上离目标点最近的点
   */
  protected findNearestPointOnLine(point: IPoint, line: ILine): IPoint {
    const { startPoint, endPoint } = line

    // 线段向量
    const dx = endPoint.x - startPoint.x
    const dy = endPoint.y - startPoint.y
    // 线段长度的平方
    const lengthSquared = dx * dx + dy * dy

    // 如果线段退化为点，直接返回起点
    if (lengthSquared === 0) {
      return { x: startPoint.x, y: startPoint.y }
    }

    // 计算投影参数 t（0 <= t <= 1表示投影点在线段上）
    const t = Math.max(
      0,
      Math.min(1, ((point.x - startPoint.x) * dx + (point.y - startPoint.y) * dy) / lengthSquared)
    )

    // 计算投影点
    return {
      x: startPoint.x + t * dx,
      y: startPoint.y + t * dy,
    }
  }

  /**
   * 计算两点之间的距离
   */
  protected calculateDistance(p1: IPoint, p2: IPoint): number {
    const dx = p1.x - p2.x
    const dy = p1.y - p2.y
    return Math.sqrt(dx * dx + dy * dy)
  }

  /**
   * 重置为默认配置
   */
  resetConfig(): void {
    this.config = { ...this.defaultConfig }
    this.config.enabledTypes = new Set(this.defaultConfig.enabledTypes)
  }

  /************ 注入工具 ************/
  /**
   * 注入宿主工具实例。
   *
   * @param tool - 当前使用该捕捉器的工具
   */
  injectTool(tool: BaseTool): void {
    this.tool = tool
  }

  /** 解除当前宿主工具的引用 */
  ejectTool(): void {
    this.tool = undefined
  }
}
